/**
 * Лента дня — общий компонент для обеих ролей.
 *
 * Родитель и няня должны видеть одно и то же: расхождение в том, «что было
 * сегодня», — это и есть источник конфликтов, которые продукт устраняет.
 * Различается только то, что можно сделать с записью.
 */

import clsx from 'clsx'
import { useMemo, type ReactNode } from 'react'

import { GeoLine } from '@/features/geo/GeoLine'
import type { ExpenseCard, GeoPoint, TimelineEvent } from '@/shared/api/day'
import { formatClock, formatTime } from '@/shared/format'
import { Icon } from '@/shared/ui/Icon'
import { Taped } from '@/shared/ui/decor'
import { eventIcon, expenseIcon } from '@/shared/ui/icon-map'
import { Card, Chip, Money } from '@/shared/ui/primitives'
import { EmptyState } from '@/shared/ui/states'

type Entry =
  | { kind: 'event'; at: string; event: TimelineEvent }
  | { kind: 'expense'; at: string; expense: ExpenseCard }

export function Timeline({
  events,
  expenses,
  timeZone,
  currency,
  emptyTitle,
  emptyDescription,
  emptyAction,
  onEventPress,
  onExpensePress,
  onPhotoPress,
  onGeoPress,
}: {
  events: TimelineEvent[]
  expenses: ExpenseCard[]
  timeZone: string
  currency: string
  emptyTitle: string
  emptyDescription?: string
  emptyAction?: ReactNode
  onEventPress?: (event: TimelineEvent) => void
  onExpensePress?: (expense: ExpenseCard) => void
  onPhotoPress?: (urls: string[], index: number) => void
  onGeoPress?: (geo: GeoPoint) => void
}) {
  const entries = useMemo<Entry[]>(() => {
    const merged: Entry[] = [
      ...events.map((event) => ({ kind: 'event' as const, at: event.occurred_at, event })),
      ...expenses.map((expense) => ({
        kind: 'expense' as const,
        at: expense.occurred_at,
        expense,
      })),
    ]
    return merged.sort((a, b) => a.at.localeCompare(b.at))
  }, [events, expenses])

  if (entries.length === 0) {
    return (
      <EmptyState
        icon="state-empty-timeline"
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    )
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) =>
        entry.kind === 'event' ? (
          <EventRow
            key={`e${entry.event.id}`}
            event={entry.event}
            timeZone={timeZone}
            onPress={onEventPress}
            onPhotoPress={onPhotoPress}
            onGeoPress={onGeoPress}
          />
        ) : (
          <ExpenseRow
            key={`x${entry.expense.id}`}
            expense={entry.expense}
            timeZone={timeZone}
            currency={currency}
            onPress={onExpensePress}
          />
        ),
      )}
    </div>
  )
}

function EventRow({
  event,
  timeZone,
  onPress,
  onPhotoPress,
  onGeoPress,
}: {
  event: TimelineEvent
  timeZone: string
  onPress?: (event: TimelineEvent) => void
  onPhotoPress?: (urls: string[], index: number) => void
  onGeoPress?: (geo: GeoPoint) => void
}) {
  const running = event.is_interval && !event.ended_at
  const minutes = running
    ? Math.floor((Date.now() - new Date(event.occurred_at).getTime()) / 60_000)
    : event.ended_at
      ? Math.floor(
          (new Date(event.ended_at).getTime() - new Date(event.occurred_at).getTime()) / 60_000,
        )
      : null

  return (
    <Card
      className={clsx('enter', onPress && 'cursor-pointer')}
      onClick={onPress ? () => onPress(event) : undefined}
    >
      <div className="flex gap-3">
        <span
          aria-hidden
          className="bg-surface-muted border-line/60 flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-button)] border"
        >
          {/* Значок берём по коду типа: event.emoji остаётся в данных ради бота */}
          <Icon name={eventIcon(event.type_code)} size={21} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-body font-semibold">{event.title}</span>
            <span className="text-text-muted tabular shrink-0 text-caption">
              {formatTime(event.occurred_at, timeZone)}
            </span>
          </div>

          {minutes !== null && (
            <div className="mt-1">
              <Chip tone={running ? 'accent' : 'neutral'} dot={running}>
                {running ? `идёт ${formatClock(minutes)}` : formatClock(minutes)}
              </Chip>
            </div>
          )}

          {event.note && (
            <p className="text-text-muted mt-1.5 text-note leading-snug">{event.note}</p>
          )}

          {event.geo && <GeoLine geo={event.geo} onPress={onGeoPress} />}

          {event.thumb_urls.length > 0 && (
            <div className="mt-3 flex gap-3 overflow-x-auto px-1 pt-2 pb-2">
              {event.thumb_urls.map((url, index) => (
                <Taped key={url} index={index} className="shrink-0">
                  <button
                    type="button"
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation()
                      onPhotoPress?.(event.photo_urls, index)
                    }}
                    className="press block"
                  >
                    <img
                      src={url}
                      alt=""
                      loading="lazy"
                      className="border-surface size-20 rounded-[var(--radius-thumb)] border-4 object-cover shadow-[var(--shadow-card)]"
                    />
                  </button>
                </Taped>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

function ExpenseRow({
  expense,
  timeZone,
  currency,
  onPress,
}: {
  expense: ExpenseCard
  timeZone: string
  currency: string
  onPress?: (expense: ExpenseCard) => void
}) {
  const tone =
    expense.status === 'approved' || expense.status === 'reimbursed'
      ? 'success'
      : expense.status === 'rejected'
        ? 'danger'
        : 'warm'
  const label =
    expense.status === 'pending'
      ? 'ожидает'
      : expense.status === 'approved'
        ? expense.auto_approved
          ? 'одобрен автоматически'
          : 'одобрен'
        : expense.status === 'reimbursed'
          ? 'компенсирован'
          : 'отклонён'

  return (
    <Card
      className={clsx('enter', onPress && 'cursor-pointer')}
      onClick={onPress ? () => onPress(expense) : undefined}
    >
      <div className="flex gap-3">
        <span
          aria-hidden
          className="bg-warm-soft border-warm/15 flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-button)] border"
        >
          {/* Незнакомая категория получает чек — запасной значок даёт expenseIcon */}
          <Icon name={expenseIcon(expense.category)} size={21} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-body font-semibold">{expense.title}</span>
            <Money value={expense.amount} currency={currency} size="md" tone="warm" />
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <Chip tone={tone}>{label}</Chip>
            <span className="text-text-muted tabular text-caption">
              {formatTime(expense.occurred_at, timeZone)}
            </span>
          </div>
          {expense.reject_reason && (
            <p className="text-text-muted mt-1.5 text-note leading-snug">
              {expense.reject_reason}
            </p>
          )}
          {expense.receipt_urls.length > 0 && (
            <div className="mt-3 flex gap-3 px-1 pt-2">
              {expense.receipt_urls.map((url, index) => (
                <Taped key={url} index={index + 2}>
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    className="border-surface size-16 rounded-[var(--radius-thumb)] border-4 object-cover shadow-[var(--shadow-card)]"
                  />
                </Taped>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
